import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import { authenticationActions as actions } from '.';
import { forgotPasswordActions } from 'features/ForgotPassword/slice';

import httpclient from 'libs/Axios/index';
import httpClientWithoutAbort from 'libs/Axios/httpClientWithoutAbort';
import { PayloadAction } from '@reduxjs/toolkit';

import History from 'core/utils/history';
import { error } from 'core/components/Notify';
import axios from 'axios';
import { API_HOST } from 'core/utils/constants/ApiConstants';
import { applyAccessDataToAuth, getFirstAccessiblePath } from 'core/utils/accessControl';


/**
 * Everything that happens once a session exists: resolve role and access,
 * persist it, route onward. Shared by password login and the onboarding
 * hand-off, which arrives with a session already minted server-side.
 */
export function* completeSignIn(data: any, ipaddress: any) {
  if (data?.CHANGE_PW) {
    // /updatePassword wants a reset token; an ACCESS_TOKEN is rejected.
    const resetToken =
      data?.PASSWORD_RESET_TOKEN || data?.PRE_AUTH_TOKEN || data?.ACCESS_TOKEN;
    yield put(forgotPasswordActions.clearState());
    yield put(forgotPasswordActions.verifyOTPSuccess({
      token: resetToken,
      isVerified: true
    }));
    yield put(actions.clearState());
    History.navigate(`/reset-password?__user_id=${data?.USER_ID}&__token=${encodeURIComponent(resetToken || '')}`);
  } else {
    if (!!data?.USER_INFO?.length) {
      if (data?.USER_INFO?.length > 1) {
        data.IS_USER_INFO = true;
      }

      if (data?.USER_INFO?.length === 1) {
        data = { ...data, ...data?.USER_INFO?.[0] }
        data.ROLE = data?.USER_ROLE_NAME;
        const response = yield axios({
          method: 'get',
          url: `${API_HOST}master/tableColumnDisplay/list?institute_id=${data?.INSTITUTE_ID}&branch_id=${data?.BRANCH_ID}`,
          headers: {
            database: data?.DATABASE,
            Authorization: `Bearer ${data?.ACCESS_TOKEN}`,
            "payment-service-name": "CASHFREE",
            "ip-address": data.IP_ADDRESS,
            "institute-id": data.INSTITUTE_ID,
            "branch-id": data.BRANCH_ID,
            "user-id": data.USER_ID,
            "user-name": data.USER_NAME,
            "user-role": data.USER_ROLE_NAME,
            "source": 'STAFF',
            "academic-year-id": data?.CURRENT_ACADEMIC_YEAR_ID,
          }
        })
        data.COLUMNS = response?.data?.data?.TableColumnDisplayList || [];
      }

      data.IS_LOGGED_IN = true;

      if (("BRANCH_SETUP" in data) && data?.BRANCH_SETUP !== 'COMPLETED') {
        const response = yield axios({
          method: 'get',
          url: `${API_HOST}/master/prerequisites`,
          headers: {
            database: data?.DATABASE,
            Authorization: `Bearer ${data?.ACCESS_TOKEN}`,
            "payment-service-name": "CASHFREE",
            "ip-address": data.IP_ADDRESS,
            "institute-id": data.INSTITUTE_ID,
            "branch-id": data.BRANCH_ID,
            "user-id": data.USER_ID,
            "user-name": data.USER_NAME,
            "user-role": data.USER_ROLE_NAME,
            "source": 'STAFF',
            "academic-year-id": data?.CURRENT_ACADEMIC_YEAR_ID,
          }
        })
        data.MASTER_DATA = response?.data?.data || {};
      }

      if (data?.USER_ROLE_NAME === "STAFF") {
        if (EncryptedStorage.getItem('_DEFAULT_ROLE_')) {
          data.STAFF_ROLE = JSON.parse(
            EncryptedStorage.getItem('_DEFAULT_ROLE_') as string,
          );
        } else if (data?.STAFF_INFO?.length === 1) {
          data.STAFF_ROLE = data?.STAFF_INFO?.[0];
        } else {
          data.STAFF_ROLE = {
            STAFF_ID: 0,
            STAFF_ROLE_ID: 0,
            STAFF_ROLE_NAME: '',
          };
        }
      }

      if (EncryptedStorage.getItem('_SELECTED_ACADEMIC_YEAR_')) {
        data.SELECTED_ACADEMIC_YEAR = JSON.parse(
          EncryptedStorage.getItem('_SELECTED_ACADEMIC_YEAR_') as string,
        );
      }
      setTokenToLocalStorage(
        data.ACCESS_TOKEN,
        JSON.stringify(data),
        data.DATABASE,
      );
      EncryptedStorage.setItem('IP_ADDRESS', ipaddress);
      try {
        const canResolveAccess = data?.USER_ROLE_NAME !== 'STAFF' || !!data?.STAFF_ROLE?.STAFF_ROLE_NAME;
        if (data?.BRANCH_ID && canResolveAccess) {
          const registryResponse = yield httpclient({ ipAddress: ipaddress }).get('/auth/access/registry');
          const accessResponse = yield httpclient({ ipAddress: ipaddress }).get('/auth/access/me');

          data = applyAccessDataToAuth(data, registryResponse?.data?.data, accessResponse?.data?.data);

          try {
            const branchRoleResponse = yield httpclient({ ipAddress: ipaddress }).get(`/data/branchRole/list/${data?.BRANCH_ID}`);
            data.BRANCH_ROLE_LIST = branchRoleResponse?.data?.data?.BranchRoleList || [];
          } catch (branchRoleError) {
            console.error('Unable to load branch roles:', branchRoleError);
          }

          EncryptedStorage.setItem('_DATA_', JSON.stringify(data));
        }
      } catch (accessError) {
        console.error('Unable to resolve access permissions:', accessError);
      }
      yield put(actions.loggingInSuccess(data));
      if (data?.BRANCH_SETUP !== 'COMPLETED' && data?.USER_INFO?.length === 1) {
        History.navigate('/initial-setup');
      } else {
        History.navigate(getFirstAccessiblePath(data?.Modules));
      }

    } else {
      error("User Role Not Found !", true);
      yield put(actions.loggingInFailure("User Role Not Found !" as any));
    }
  }
}

function* login(
  action: PayloadAction<{
    data: { USER_NAME: string; PASSWORD: string; SOURCE: string; DEVICE_TYPE?: string; LOCATION?: string; FORCE_LOGIN_ID?: number[] };
    cancelToken: any;
  }>,
) {
  try {
    const ipaddress = yield fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then((res: any) => {
        return res.ip;
      })
      .catch(error => {
        console.error('Error fetching IP address:', error);
      });

    const loginPayload = {
      ...action.payload.data,
      DEVICE_TYPE: action.payload.data.DEVICE_TYPE || 'WEB',
    };

    const response = yield httpclient({ ipAddress: ipaddress }).post('/auth/login/web/institutes', loginPayload, {
      cancelToken: action.payload?.cancelToken?.token || false,
    });

    let data = response?.data?.data;

    data.IP_ADDRESS = String(ipaddress)

    if (data?.CHANGE_PW) {
      const resetToken = data?.ACCESS_TOKEN || data?.token || data?.PRE_AUTH_TOKEN;
      const resetUserId = data?.USER_ID || data?.USER_NAME || loginPayload.USER_NAME;

      yield put(forgotPasswordActions.clearState());
      yield put(forgotPasswordActions.verifyOTPSuccess({
        token: resetToken,
        isVerified: true
      }));
      yield put(actions.clearState());
      History.navigate(`/reset-password?__user_id=${resetUserId}&__token=${encodeURIComponent(resetToken || '')}`);
      return;
    }

    const institutes = data?.INSTITUTES || [];

    if (institutes?.length > 1) {
      const selectionData = {
        ...data,
        USER_NAME: loginPayload.USER_NAME,
        SOURCE: loginPayload.SOURCE,
        LOCATION: loginPayload.LOCATION,
        DEVICE_TYPE: loginPayload.DEVICE_TYPE,
        FORCE_LOGIN_ID: loginPayload.FORCE_LOGIN_ID || null,
        IS_LOGGED_IN: true,
        IS_INSTITUTE_SELECTION_PENDING: true,
        USER_INFO: [],
      };

      EncryptedStorage.setItem('_DATA_', JSON.stringify(selectionData));
      EncryptedStorage.setItem('IP_ADDRESS', ipaddress);
      yield put(actions.loggingInSuccess(selectionData));
      History.navigate('/dashboard');
      return;
    }

    if (institutes?.length === 1) {
      const selectedInstitute = institutes[0];
      const selectInstitutePayload = {
        PRE_AUTH_TOKEN: data?.PRE_AUTH_TOKEN,
        INSTITUTE_ID: selectedInstitute?.INSTITUTE_ID,
        BRANCH_ID: selectedInstitute?.BRANCH_ID,
        SOURCE: loginPayload.SOURCE,
        DEVICE_TYPE: loginPayload.DEVICE_TYPE,
        FORCE_LOGIN_ID: loginPayload.FORCE_LOGIN_ID || null,
        LOCATION: loginPayload.LOCATION || '',
      };

      const selectInstituteResponse = yield httpclient({ ipAddress: ipaddress }).post(
        '/auth/login/web/select-institute',
        selectInstitutePayload,
        {
          cancelToken: action.payload?.cancelToken?.token || false,
        },
      );

      data = {
        ...selectInstituteResponse?.data?.data,
        IP_ADDRESS: String(ipaddress),
        SELECTED_WEB_INSTITUTE: selectedInstitute,
      };
    } else {
      error("Institute Not Found !", true);
      yield put(actions.loggingInFailure("Institute Not Found !" as any));
      return;
    }

    yield call(completeSignIn, data, ipaddress);

  } catch (error: any) {
    if (error?.response?.data?.code === 409) {
      yield put(actions.sessionLimitReached(error.response.data));
    } else {
      const errorDetails = error?.response?.message || error?.message;
      yield put(actions.loggingInFailure(errorDetails));
    }
  }
}

function* logout(action: PayloadAction<{ USER_LOGIN_ID: number, cancelToken: any, isCancelFlow?: boolean }>) {
  try {
    const response = yield httpClientWithoutAbort().post('/auth/logout', action.payload, {
      cancelToken: action.payload?.cancelToken?.token || false,
    });
    const data = response?.data?.data;
    resetLocalStorage();
    yield delay(50);
    resetLocalStorage();

    yield put(actions.logoutSuccess({ ...data, isCancelFlow: action.payload.isCancelFlow }));
  } catch (error: any) {
    const errorDetails = error?.response?.message || error?.message;
    resetLocalStorage();
    yield delay(50);
    resetLocalStorage();
    yield put(actions.logoutFailure({ error: errorDetails, isCancelFlow: action.payload.isCancelFlow }));
  }
}

export function setTokenToLocalStorage(
  token: string,
  data?: string,
  database?: string,
) {
  EncryptedStorage.setItem('_UFT_', `Bearer ${token}`);
  if (data) EncryptedStorage.setItem('_DATA_', data);
  if (database) EncryptedStorage.setItem('_DATABASE_', database);
}

export function resetLocalStorage() {
  document.cookie.split(';').forEach(function (item) {
    document.cookie = item.replace(/^([^=]+).*=/, '$1=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/');
  });

  const clearStorageExceptTablePreferences = (storage: Storage) => {
    const tablePreferences: Array<[string, string]> = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith('vd_table_')) {
        const value = storage.getItem(key);
        if (value !== null) tablePreferences.push([key, value]);
      }
    }

    storage.clear();
    tablePreferences.forEach(([key, value]) => storage.setItem(key, value));
  };

  clearStorageExceptTablePreferences(localStorage);
  clearStorageExceptTablePreferences(sessionStorage);
  EncryptedStorage.clearCache();
}

/**
 * Adopt a session minted elsewhere. The onboarding wizard runs on another
 * origin, so it trades its onboarding session for a real one and hands the
 * result over. From here this is an ordinary sign-in.
 */
function* sessionHandoff(action: PayloadAction<{ session: any }>) {
  try {
    const ipaddress = yield fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then((res: any) => res.ip)
      .catch(err => {
        console.error('Error fetching IP address:', err);
      });

    const data = {
      ...action.payload.session,
      IP_ADDRESS: String(ipaddress),
    };

    yield call(completeSignIn, data, ipaddress);
  } catch (err: any) {
    const errorDetails = err?.response?.message || err?.message;
    yield put(actions.loggingInFailure(errorDetails));
    History.navigate('/login');
  }
}

export function* authenticationSaga() {
  yield all([
    takeLatest(actions.loggingInStart.type, login),
    takeLatest(actions.sessionHandoffStart.type, sessionHandoff),
    takeLatest(actions.logoutStart.type, logout),
  ]);
}
